import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/auth";
import { ROLE_LABELS } from "@/lib/access";

const API = `${window.location.origin}/api`;
const EMPTY = { name: "", email: "", password: "", role: "sales", can_edit_rates: false, team_member: "" };
const AUTO = "__auto";

export function AddUserDialog({ open, onOpenChange, onCreated, teamMembers = [], googleOnly = false }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const usesGoogle = googleOnly && form.role === "sales";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { toast.error("Enter a name and a valid email"); return; }
    if (!usesGoogle && form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/users`, { ...form, email: form.email.trim(), password: usesGoogle ? "" : form.password });
      toast.success(`${form.name} added — they can now log in`);
      setForm(EMPTY);
      onOpenChange(false);
      onCreated(res.data);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]" data-testid="add-user-dialog">
        <DialogHeader><DialogTitle className="text-lg font-bold">Add User Login</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-1">
          <div><Label className="text-sm font-semibold">Full Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="h-10" data-testid="new-user-name" /></div>
          <div><Label className="text-sm font-semibold">Email</Label><Input type="email" autoCapitalize="none" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-10" data-testid="new-user-email" /></div>
          {usesGoogle ? (
            <p className="text-xs p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-800" data-testid="google-only-note">
              Sales Team must sign in with Google (Settings → Google Sign-in). Enter their Google / Gmail address above — no password needed.
            </p>
          ) : (
            <div><Label className="text-sm font-semibold">Password</Label><Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Min 6 characters" className="h-10" data-testid="new-user-password" /></div>
          )}
          <div>
            <Label className="text-sm font-semibold">Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="h-10" data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.role === "sales" && (
            <div>
              <Label className="text-sm font-semibold">Linked Team Member</Label>
              <Select value={form.team_member || AUTO} onValueChange={(v) => set("team_member", v === AUTO ? "" : v)}>
                <SelectTrigger className="h-10" data-testid="new-user-team-member"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUTO}>Auto (match by email)</SelectItem>
                  {teamMembers.map((m) => <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-500 mt-1">They will see leads & customers assigned to this team member, plus unassigned ones.</p>
            </div>
          )}
          {form.role === "sales" && (
            <label className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-gray-50">
              <span className="text-sm text-gray-700">Allow editing product rates</span>
              <Switch checked={form.can_edit_rates} onCheckedChange={(v) => set("can_edit_rates", v)} data-testid="new-user-edit-rates" />
            </label>
          )}
          <Button type="submit" disabled={saving} className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="save-new-user-btn">
            {saving ? "Creating..." : "Create User"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ResetPasswordDialog({ user, onClose, onSave }) {
  const [pw, setPw] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    onSave(pw);
    setPw("");
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) { setPw(""); onClose(); } }}>
      <DialogContent className="sm:max-w-[380px]" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle className="text-lg font-bold">Reset Password — {user?.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-1">
          <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 6)" className="h-10" data-testid="reset-user-password-input" />
          <Button type="submit" className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="save-reset-password-btn">Set New Password</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
